import cn from 'classnames'
import React, { ComponentProps, ReactElement } from 'react'
import { amexCardTypes } from 'src/app/RegisterAccount/RegisterAccount'
import SvgPencilIcon from 'src/components/Svgs/SvgPencilIcon'
import { AmexCardTypeFields } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'

import style from './CardDisplayBox.style'

type CreditCardData = {
  cardName: string
  cardholderName: string
  number: string
  expiration: string
  cvc?: string
  address: Record<string, string>
  cardType?: AmexCardTypeFields
  issuerImg?: string
}

type CardDisplayBoxProps = {
  className?: string
  cardData: CreditCardData
  /** When provided, renders a clickable Edit link with a pencil icon in the top-right of the card row. */
  onEdit?: () => void
} & ComponentProps<'div'>

// Generated with Google Gemini
function maskCreditCard(cardNumberString: string): string {
  // The regular expression finds any digit (\d) that is followed by
  // at least four more digits somewhere later in the string.
  // The `g` flag ensures all such digits are replaced, not just the first.
  const regex = /\d(?=.*\d{4})/g
  return cardNumberString.replace(regex, '*')
}

function CardDisplayBox(props: CardDisplayBoxProps): ReactElement {
  const { className = '', cardData, onEdit, ...rest } = props
  const translate = useTranslate()

  const maskedCardNumber = maskCreditCard(cardData.number)

  const selectedCardType =
    cardData.address.cardType &&
    amexCardTypes.find(
      (amexCardType) => amexCardType.value === cardData.address.cardType,
    )

  return (
    <div className={cn('CardDisplayBox', className)} css={style} {...rest}>
      <div className="card-row">
        <div className="card-identity">
          {cardData.issuerImg && (
            <img
              className="issuer-img"
              src={cardData.issuerImg}
              alt="Credit Card"
            />
          )}
          <div className="card-identity-text">
            <p className="font-bold">{cardData.cardName}</p>
            <p>{cardData.cardholderName}</p>
            <p>{maskedCardNumber}</p>
          </div>
        </div>
        <div className="card-meta">
          <div className="meta-cell">
            <p className="font-form-label meta-label">
              {translate('Expiration')}
            </p>
            <p>{cardData.expiration}</p>
            {selectedCardType && (
              <p className="card-type-spacer">{selectedCardType.fieldText}</p>
            )}
          </div>
          {cardData.cvc !== undefined && (
            <div className="meta-cell">
              <p className="font-form-label meta-label">{translate('CVC')}</p>
              <p>{cardData.cvc}</p>
            </div>
          )}
        </div>
        {onEdit && (
          <button
            type="button"
            className="card-edit-link"
            data-testid="card-edit-link"
            onClick={onEdit}
          >
            <span>{translate('Edit')}</span>
            <SvgPencilIcon />
          </button>
        )}
      </div>
      <div className="address-grid">
        <div className="address-column">
          <p>{cardData.address['address1']}</p>
          <p>{cardData.address['city']}</p>
          <p>{cardData.address['postal']}</p>
        </div>
        <div className="address-column">
          <p>{cardData.address['address2'] || ' '}</p>
          <p>{cardData.address['province']}</p>
          <p>{cardData.address['country']}</p>
        </div>
      </div>
    </div>
  )
}

export default CardDisplayBox
